import { Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  formatRate,
  type ReviewsReputationView,
} from '../../../lib/reporting/community';
import { CHANNEL_LABEL } from '../../../lib/inboxView';
import { ChannelIcon } from '../../../components/ui/ChannelIcon';
import { StarRating } from '../../community';
import { DeltaChip } from './SocialCommunityTab';

// Community & Reputation §3 — Reviews & reputation. Reviews received, the
// PERIOD-SCOPED average rating (never a claimed overall/lifetime rating), star
// distribution, reply rate, a rating trend, and the recent reviews.
//
// ⚠️ THE RATING IS PERIOD-SCOPED. We hold only a recent ingested slice of reviews
// and the GBP batch endpoint omits the true aggregate rating, so we CANNOT compute
// a business's overall star rating. The average shown is of reviews RECEIVED THIS
// PERIOD, labelled as such — a period-average dressed up as "your Google rating"
// would be a materially false claim to a client. The period filter is enforced +
// mutation-tested in the pure lib (periodAverageRating).

const TREND_CONFIG: ChartConfig = {
  count: { label: 'Reviews', color: 'hsl(217 91% 70%)' },
  averageRating: { label: 'Avg rating', color: 'hsl(38 92% 50%)' },
};

function labelWeek(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

function labelDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

// Reviews & reputation snapshot — reused for BOTH surfaces via the title/subtitle
// props: the Google Business tab renders the Google-review reputation (default
// copy), the Community tab renders the Facebook-review item. The emptyMessage lets
// each surface phrase its own no-reviews state.
export function CommunityReviewsSection({
  view,
  title = 'Reviews & reputation',
  subtitle = 'Facebook & Google reviews received this period, and how we responded.',
  emptyMessage = 'No reviews received for this client in the selected period.',
}: {
  view: ReviewsReputationView;
  title?: string;
  subtitle?: string;
  emptyMessage?: string;
}) {
  if (view.isEmpty) {
    return (
      <Shell title={title} subtitle={subtitle}>
        <p className="rounded-md bg-slate-50 px-3 py-6 text-center text-sm text-slate-400">
          {emptyMessage}
        </p>
      </Shell>
    );
  }

  return (
    <Shell title={title} subtitle={subtitle}>
      <div className="space-y-4">
        {/* Headline stats. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Reviews received
            </p>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-semibold tabular-nums text-slate-900">
                {view.reviewsReceived.current.toLocaleString()}
              </span>
              <DeltaChip delta={view.reviewsReceived} />
            </div>
            <p className="mt-1 text-xs text-slate-400">
              vs {view.reviewsReceived.previous.toLocaleString()} last period
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Average rating
            </p>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-2xl font-semibold tabular-nums text-slate-900">
                {view.averageRating === null ? '—' : view.averageRating.toFixed(1)}
              </span>
              {view.averageRating !== null && (
                <StarRating rating={view.averageRating} className="mt-0.5" />
              )}
            </div>
            {/* The honesty label — period-scoped, never claimed as overall. */}
            <p className="mt-1 text-xs text-slate-400">{view.averageRatingNote}</p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Reply rate</p>
            <div className="mt-2">
              <span className="text-2xl font-semibold tabular-nums text-slate-900">
                {formatRate(view.replyRate)}
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-400">Reviews we replied to this period</p>
          </div>
        </div>

        {/* Star distribution + rating trend. */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,18rem)_1fr]">
          <StarDistribution view={view} />
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
              Rating trend
            </p>
            {view.ratingTrend.length === 0 ? (
              <div className="flex h-48 items-center justify-center rounded-md bg-slate-50 text-sm text-slate-400">
                Not enough reviews to chart a trend.
              </div>
            ) : (
              <ChartContainer config={TREND_CONFIG} className="aspect-auto h-48 w-full">
                <ComposedChart
                  data={view.ratingTrend}
                  margin={{ left: 4, right: 8, top: 4, bottom: 0 }}
                  accessibilityLayer
                >
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis
                    dataKey="weekStart"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tickFormatter={labelWeek}
                  />
                  <YAxis
                    yAxisId="count"
                    width={24}
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    yAxisId="rating"
                    orientation="right"
                    domain={[0, 5]}
                    ticks={[0, 1, 2, 3, 4, 5]}
                    width={24}
                    tickLine={false}
                    axisLine={false}
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        labelFormatter={(v) => `Week of ${labelWeek(String(v))}`}
                      />
                    }
                  />
                  <Bar
                    yAxisId="count"
                    dataKey="count"
                    fill="var(--color-count)"
                    radius={[2, 2, 0, 0]}
                    maxBarSize={28}
                    isAnimationActive={false}
                  />
                  <Line
                    yAxisId="rating"
                    type="monotone"
                    dataKey="averageRating"
                    stroke="var(--color-averageRating)"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ChartContainer>
            )}
          </div>
        </div>

        {/* Recent reviews. */}
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
            Recent reviews
          </p>
          <ul className="divide-y divide-slate-100">
            {view.recentReviews.map((r) => (
              <li key={r.id} className="flex items-start gap-3 py-2.5">
                <ChannelIcon channel={r.channel} className="mt-0.5 h-4 w-4" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <StarRating rating={r.rating} />
                    <span className="text-sm font-medium text-slate-800">{r.authorName}</span>
                    <span className="text-xs text-slate-400">· {labelDay(r.at)}</span>
                    <span
                      className={
                        r.replied
                          ? 'rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700'
                          : 'rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700'
                      }
                    >
                      {r.replied ? 'Replied' : 'Awaiting reply'}
                    </span>
                  </div>
                  {r.text && <p className="mt-1 text-sm text-slate-600">{r.text}</p>}
                </div>
                {r.permalink && (
                  <a
                    href={r.permalink}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 text-xs font-medium text-kyck-600 hover:underline"
                  >
                    View
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Shell>
  );
}

function Shell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
      </div>
      {children}
    </section>
  );
}

function StarDistribution({ view }: { view: ReviewsReputationView }) {
  const max = Math.max(1, ...view.starDistribution.map((r) => r.count));
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
        Star distribution
      </p>
      <div className="space-y-1.5">
        {view.starDistribution.map((row) => (
          <div key={row.stars} className="flex items-center gap-2 text-sm">
            <span className="w-3 tabular-nums text-slate-500">{row.stars}</span>
            <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-amber-400"
                style={{ width: `${(row.count / max) * 100}%` }}
              />
            </div>
            <span className="w-6 text-right tabular-nums text-slate-600">{row.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
